import { forwardRef } from "react";

// Format DLUO from any date format to MM/AA (MM/YY)
export function formatDluo(input) {
  if (!input) return "—";
  const s = String(input).trim();
  if (/^\d{2}\/\d{2}$/.test(s)) return s;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[2]}/${iso[1].slice(-2)}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${mm}/${yy}`;
  }
  return s;
}

export const LabelPreview = forwardRef(function LabelPreview({ data, scale = 4 }, ref) {
  const w = (data.label_width_mm || 60) * scale;
  const h = (data.label_height_mm || 40) * scale;
  // Reserved logo slot width as % of label width (10..60), height uses same %
  const logoPct = Math.max(10, Math.min(60, Number(data.logo_size_percent) || 25));
  const hasLogo = !!data.logo_base64;
  const logoSlotW = hasLogo ? (w * logoPct) / 100 : 0;

  return (
    <div
      ref={ref}
      data-testid="label-preview"
      style={{
        width: w,
        height: h,
        background: "#ffffff",
        color: "#000000",
        border: "1px solid #000",
        position: "relative",
        padding: "8px 10px",
        fontFamily: "IBM Plex Sans, sans-serif",
        boxSizing: "border-box",
      }}
    >
      {/* Text column — leaves reserved space on the right for the logo slot */}
      <div style={{ paddingRight: logoSlotW ? logoSlotW + 6 : 0 }}>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 700, fontSize: Math.max(11, w / 22), lineHeight: 1.05 }}>
          {data.brand || "—"}
        </div>
        <div style={{ fontSize: Math.max(9, w / 30), lineHeight: 1.2, marginTop: 2 }}>
          {data.flavor || ""}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 4, fontWeight: 600, fontSize: Math.max(9, w / 30) }}>
          <span data-testid="preview-bottle-size">{data.bottle_size_ml} ml</span>
          {data.nicotine_mg != null && data.nicotine_mg !== "" && (
            <span>· Nicotine {data.nicotine_mg} mg/ml</span>
          )}
        </div>

        <div style={{ marginTop: 4, fontFamily: "JetBrains Mono, monospace", fontSize: Math.max(7, w / 40), lineHeight: 1.35 }}>
          <div>UFI: <b>{data.ufi || "N/A"}</b></div>
          <div>Lot: <b>{data.batch_number || "—"}</b></div>
          <div>DLUO: <b>{formatDluo(data.dluo_date)}</b></div>
        </div>

        <div style={{ marginTop: 3, fontSize: Math.max(6, w / 45), lineHeight: 1.3 }}>
          <div>Mis en bouteille par : <b>{data.shop_name}</b></div>
          {data.shop_address && <div>{data.shop_address}</div>}
        </div>
      </div>

      {/* Reserved logo slot — top-right, doesn't overlap text */}
      {hasLogo && (
        <div
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: logoSlotW,
            height: logoSlotW,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <img
            src={data.logo_base64}
            alt="logo"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        </div>
      )}

      {/* Bottom pictograms + regulation */}
      <div style={{
        position: "absolute", left: 8, right: 8, bottom: 4,
        display: "flex", alignItems: "center", gap: 6,
      }}>
        {data.show_pictograms && (
          <>
            <PictoNoUnder18 size={Math.max(18, w / 12)} />
            <PictoNoPregnant size={Math.max(18, w / 12)} />
          </>
        )}
        <div style={{ fontSize: Math.max(5.5, w / 55), lineHeight: 1.15, fontWeight: 600, flex: 1 }}>
          Interdit aux mineurs de moins de 18 ans.<br />
          Déconseillé aux femmes enceintes ou allaitantes.
        </div>
      </div>
    </div>
  );
});

function PictoNoUnder18({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-label="Interdit aux -18 ans">
      <circle cx="16" cy="16" r="14" fill="#fff" stroke="#000" strokeWidth="2.4" />
      <text x="16" y="20" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="12" textAnchor="middle" fill="#000">18</text>
      <line x1="4.5" y1="27.5" x2="27.5" y2="4.5" stroke="#e11d48" strokeWidth="2.8" />
    </svg>
  );
}

function PictoNoPregnant({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-label="Interdit aux femmes enceintes">
      <circle cx="16" cy="16" r="14" fill="#fff" stroke="#000" strokeWidth="2.4" />
      <circle cx="15" cy="9" r="2" fill="#000" />
      <path d="M12 12 Q11 17 12 22 L12 26 L14 26 L14 22 Q15 21 16 21 Q17 21 18 22 L18 26 L20 26 L20 22 Q22 18 20 14 Q19 12 15 12 Z" fill="#000" />
      <line x1="4.5" y1="27.5" x2="27.5" y2="4.5" stroke="#e11d48" strokeWidth="2.8" />
    </svg>
  );
}
